type BlueprintAdminEnvironmentLabel = "DEVELOP" | "LOCAL" | "MU-DEVELOP" | "PRODUCTION" | "STAGING";

type MergeEnvironmentConfig = {
  apiBaseUrl: string;
  dashboardBaseUrl: string | null;
  kibanaBaseUrl: string;
  blueprintAdminEnvironment: BlueprintAdminEnvironmentLabel;
  djangoAdminBaseUrl?: string;
};

const LOCAL_API_BASE_URL = "http://localhost:8000";
const LOCAL_DJANGO_ADMIN_BASE_URL = "http://localhost:8000/admin";
const LOCAL_KIBANA_BASE_URL = "localhost:3080";

const mergeEnvironmentConfigs: Record<string, MergeEnvironmentConfig> = {
  LOCAL: {
    apiBaseUrl: LOCAL_API_BASE_URL,
    dashboardBaseUrl: null,
    kibanaBaseUrl: LOCAL_KIBANA_BASE_URL,
    blueprintAdminEnvironment: "LOCAL",
    djangoAdminBaseUrl: LOCAL_DJANGO_ADMIN_BASE_URL,
  },
  PRODUCTION: {
    apiBaseUrl: "https://api.merge.dev",
    dashboardBaseUrl: "https://app.merge.dev",
    kibanaBaseUrl: "https://kibana.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  DEVELOP: {
    apiBaseUrl: "https://api-develop.merge.dev",
    dashboardBaseUrl: "https://develop.merge.dev",
    kibanaBaseUrl: "https://kibana-develop.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "DEVELOP",
  },
  "MU-DEVELOP": {
    apiBaseUrl: "https://api-mu-develop.merge.dev",
    dashboardBaseUrl: "https://app-mu-develop.merge.dev",
    kibanaBaseUrl: "https://kibana-mu-develop.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "MU-DEVELOP",
  },
  "EU-TENANT": {
    apiBaseUrl: "https://api-eu.merge.dev",
    dashboardBaseUrl: "https://app-eu.merge.dev",
    kibanaBaseUrl: "https://kibana-production-eu.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "AP-TENANT": {
    apiBaseUrl: "https://api-ap.merge.dev",
    dashboardBaseUrl: "https://app-ap.merge.dev",
    kibanaBaseUrl: "https://kibana-prod-apse1.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "OYSTER-EU": {
    apiBaseUrl: "https://api-oyster-eu.merge.dev",
    dashboardBaseUrl: "https://app-oyster-eu.merge.dev",
    kibanaBaseUrl: "https://kibana-oyster.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "UBER-USW2": {
    apiBaseUrl: "https://api-uber-usw2.merge.dev",
    dashboardBaseUrl: "https://app-uber-usw2.merge.dev",
    kibanaBaseUrl: "https://kibana-uber-usw2.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "AMEX-USE1": {
    apiBaseUrl: "https://api-amex-use1.merge.dev",
    dashboardBaseUrl: "https://app-amex-use1.merge.dev",
    kibanaBaseUrl: "https://kibana-amex-use1.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "DBX-USW2": {
    apiBaseUrl: "https://api-usw2.dropboxmerge.com",
    dashboardBaseUrl: "https://app-usw2.dropboxmerge.com",
    kibanaBaseUrl: "https://kibana-dbx-usw2.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "OAI-USW2": {
    apiBaseUrl: "https://api-oai-usw2.openaimerge.com",
    dashboardBaseUrl: "https://app-oai-usw2.openaimerge.com",
    kibanaBaseUrl: "https://kibana-oai-usw2.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "OAI-EUW1": {
    apiBaseUrl: "https://api-oai-euw1.openaimerge.com",
    dashboardBaseUrl: "https://app-oai-euw1.openaimerge.com",
    kibanaBaseUrl: "https://kibana-oai-euw1.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "OAI-APNE2": {
    apiBaseUrl: "https://api-oai-apne2.openaimerge.com",
    dashboardBaseUrl: "https://app-oai-apne2.openaimerge.com",
    kibanaBaseUrl: "https://kibana-oai-apne2.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "OAI-APNE3": {
    apiBaseUrl: "https://api-oai-apne3.openaimerge.com",
    dashboardBaseUrl: "https://app-oai-apne3.openaimerge.com",
    kibanaBaseUrl: "https://kibana-oai-apne3.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
  "OAI-APSE2": {
    apiBaseUrl: "https://api-oai-apse2.openaimerge.com",
    dashboardBaseUrl: "https://app-oai-apse2.openaimerge.com",
    kibanaBaseUrl: "https://kibana-oai-apse2.access.teleport.merge.land:3080",
    blueprintAdminEnvironment: "PRODUCTION",
  },
};

export const normalizeMergeEnvironment = (value: string | undefined): string | undefined =>
  value?.trim().toUpperCase();

export const getMergeEnvironmentConfig = (
  mergeEnvironment = process.env.REACT_APP_MERGE_ENV
): MergeEnvironmentConfig | undefined => {
  const normalizedMergeEnvironment = normalizeMergeEnvironment(mergeEnvironment);
  return normalizedMergeEnvironment
    ? mergeEnvironmentConfigs[normalizedMergeEnvironment]
    : undefined;
};

export const getApiBaseUrl = (): string =>
  getMergeEnvironmentConfig()?.apiBaseUrl || LOCAL_API_BASE_URL;

export const getApiHostname = (): string => new URL(getApiBaseUrl()).hostname;

export const getApiDomain = (): string => {
  if (normalizeMergeEnvironment(process.env.REACT_APP_MERGE_ENV) === "SANDBOX") {
    return "https://api-sandbox.merge.dev";
  }

  return getApiBaseUrl();
};

export const getDjangoAdminBaseUrlForEnvironment = (): string => {
  const environmentConfig = getMergeEnvironmentConfig();

  if (!environmentConfig) {
    return LOCAL_DJANGO_ADMIN_BASE_URL;
  }

  return environmentConfig.djangoAdminBaseUrl || `${environmentConfig.apiBaseUrl}/3pmner`;
};

export const getDashboardBaseUrl = (): string | null =>
  getMergeEnvironmentConfig()?.dashboardBaseUrl || null;

export const getKibanaBaseUrlForEnvironment = (): string =>
  getMergeEnvironmentConfig()?.kibanaBaseUrl || LOCAL_KIBANA_BASE_URL;

export const getBlueprintAdminEnvironmentLabel = (): BlueprintAdminEnvironmentLabel | undefined =>
  getMergeEnvironmentConfig()?.blueprintAdminEnvironment;
